// Nines (5-3-1) runner. 3-ball game — nine points are split per hole
// based on rank. Splits are fixed:
//
//   • outright 1st, outright 2nd     → 5 / 3 / 1
//   • outright 1st, tie for 2nd      → 5 / 2 / 2
//   • tie for 1st (two players)      → 4 / 4 / 1
//   • three-way tie                  → 3 / 3 / 3
//
// Scoring basis is gross, net, or both ("netGross"). When Net & Gross
// is selected the runner returns two parallel sections — same shape
// as Skins — and the leaderboard renders them side by side.
//
// Settlement modes (per section):
//   • Bet per point (Nassau unticked). Each event triggers the
//     symmetric transfer of points → cash: every opponent pays
//     (points − opponentPoints) × pointBet to the player. Players'
//     winnings nets to zero across the table. Section TOTAL is the
//     sum of all per-hole nets.
//   • Nassau. Three pots — front 9, back 9, overall 18 — each sized
//     `players.length × bet`. Highest segment-point total wins each
//     pot; ties split. Bet-per-point math is skipped.
//
// minPlayers/maxPlayers gates the catalog entry to 3-player rounds,
// but the runner is defensive: if it ever gets called with !==3
// players, it returns an empty result rather than crashing.

import { Course, Hole, Player, ScoreMap } from "../types";

export type NinesScoringMode = "net" | "gross" | "netGross";

export interface NinesOptions {
  scoringMode?: NinesScoringMode;
  pointBet?: number;
  nassau?: boolean;
  overallBet?: number;
  frontNineBet?: number;
  backNineBet?: number;
}

// One player's payout in a single section (gross or net).
export interface NinesPayout {
  // Total Nines points the player picked up across the round.
  points: number;
  // 1-based hole numbers where the player scored — used for the
  // click-to-expand detail beneath the row. Empty when no points yet.
  pointHoles: number[];
  // Front/back point totals — surfaced as OUT/IN labels in Nassau
  // mode; informational in bet-per-point mode.
  frontPoints: number;
  backPoints: number;
  // Bet-per-point: net cash flow. Nassau: 0 (pots settle on .front/
  // .back/.overall instead).
  winnings: number;
  // Nassau pot results — front, back, overall — in cash. 0 in
  // bet-per-point mode.
  front: number;
  back: number;
  overall: number;
  // Section total (winnings in bet-per-point, front+back+overall in
  // Nassau). The leaderboard reads this for the TOTAL column.
  total: number;
}

export interface NinesSection {
  payouts: Record<string, NinesPayout>;
  nassauActive: boolean;
  hasAnyBet: boolean;
}

export interface NinesResult {
  // Always present — one of these is empty depending on scoringMode.
  gross: NinesSection;
  net: NinesSection;
  scoringMode: NinesScoringMode;
  hasAnyBet: boolean;
}

export interface NinesInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: NinesOptions;
}

// WHS-style course handicap — same shape used in skins.ts and
// stableford.ts. Duplicated here to keep the runner self-contained.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

// Apply the fixed 9-point split to a 3-player hole, given their
// scores. Returns the per-player point award keyed by player id.
// Lower score = better. Missing scores skip the hole entirely.
function splitNinePoints(
  entries: Array<{ id: string; score: number }>,
): Record<string, number> | null {
  if (entries.length !== 3) return null;
  const sorted = [...entries].sort((a, b) => a.score - b.score);
  const [s0, s1, s2] = sorted;
  const out: Record<string, number> = {};

  // All three tied — 3/3/3.
  if (s0.score === s1.score && s1.score === s2.score) {
    out[s0.id] = 3;
    out[s1.id] = 3;
    out[s2.id] = 3;
    return out;
  }
  // Tie for first (two players) — 4/4/1.
  if (s0.score === s1.score) {
    out[s0.id] = 4;
    out[s1.id] = 4;
    out[s2.id] = 1;
    return out;
  }
  // Tie for second — 5/2/2.
  if (s1.score === s2.score) {
    out[s0.id] = 5;
    out[s1.id] = 2;
    out[s2.id] = 2;
    return out;
  }
  // No ties — 5/3/1.
  out[s0.id] = 5;
  out[s1.id] = 3;
  out[s2.id] = 1;
  return out;
}

function emptyPayout(): NinesPayout {
  return {
    points: 0,
    pointHoles: [],
    frontPoints: 0,
    backPoints: 0,
    winnings: 0,
    front: 0,
    back: 0,
    overall: 0,
    total: 0,
  };
}

function emptySection(): NinesSection {
  return { payouts: {}, nassauActive: false, hasAnyBet: false };
}

// Settle one section (gross or net) given a per-(player, hole) score
// lookup. Mirrors the structure used in skins.ts but uses the fixed
// Nines split rules.
function settleSection(
  course: Course,
  players: Player[],
  pointBet: number,
  nassau: boolean,
  overallBet: number,
  frontBet: number,
  backBet: number,
  scoreFn: (playerId: string, hole: Hole) => number | null,
): NinesSection {
  const payouts: Record<string, NinesPayout> = {};
  for (const p of players) payouts[p.id] = emptyPayout();

  // Walk each hole, compute the 9-point split (when all three players
  // have a score), and update bucket totals + bet-per-point cash.
  for (const hole of course.holes) {
    const entries: Array<{ id: string; score: number }> = [];
    for (const p of players) {
      const s = scoreFn(p.id, hole);
      if (s == null) continue;
      entries.push({ id: p.id, score: s });
    }
    if (entries.length !== 3) continue;
    const award = splitNinePoints(entries);
    if (!award) continue;

    for (const id in award) {
      const pts = award[id];
      payouts[id].points += pts;
      if (pts > 0) payouts[id].pointHoles.push(hole.number);
      if (hole.number <= 9) payouts[id].frontPoints += pts;
      else payouts[id].backPoints += pts;
    }

    // Bet-per-point transfers. Each ordered (player, opponent) pair
    // contributes (myPts − theirPts) × pointBet to the player's net.
    // Summed over both opponents this is the player's hole net, and
    // across all three players the totals zero-sum.
    if (!nassau && pointBet > 0) {
      for (const e of entries) {
        for (const op of entries) {
          if (op.id === e.id) continue;
          const myPts = award[e.id];
          const opPts = award[op.id];
          payouts[e.id].winnings += (myPts - opPts) * pointBet;
        }
      }
    }
  }

  if (nassau) {
    const settle = (
      ptsFor: (pid: string) => number,
      bet: number,
      bucket: "overall" | "front" | "back",
    ) => {
      if (bet <= 0) return;
      const seg = players.map((p) => ({ id: p.id, pts: ptsFor(p.id) }));
      const maxPts = Math.max(...seg.map((s) => s.pts));
      const winners = seg.filter((s) => s.pts === maxPts);
      const pot = seg.length * bet;
      const share = pot / winners.length;
      for (const sp of seg) {
        payouts[sp.id][bucket] +=
          sp.pts === maxPts ? share - bet : -bet;
      }
    };

    settle((pid) => payouts[pid].points, overallBet, "overall");
    settle((pid) => payouts[pid].frontPoints, frontBet, "front");
    settle((pid) => payouts[pid].backPoints, backBet, "back");

    for (const p of players) {
      const pay = payouts[p.id];
      pay.total = pay.overall + pay.front + pay.back;
    }
  } else if (pointBet > 0) {
    for (const p of players) {
      const pay = payouts[p.id];
      pay.total = pay.winnings;
    }
  }

  const hasAnyBet = nassau
    ? overallBet > 0 || frontBet > 0 || backBet > 0
    : pointBet > 0;

  return { payouts, nassauActive: nassau, hasAnyBet };
}

export function runNines(input: NinesInput): NinesResult {
  const { course, players, scores, options } = input;
  const scoringMode: NinesScoringMode =
    (options.scoringMode as NinesScoringMode) || "net";
  const pointBet = Number(options.pointBet) || 0;
  const nassau = !!options.nassau;
  const overallBet = nassau ? Number(options.overallBet) || 0 : 0;
  const frontBet = nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = nassau ? Number(options.backNineBet) || 0 : 0;

  // Defensive — Nines only makes sense with exactly 3 players. Return
  // empty sections if the caller ignores the catalog gate.
  if (players.length !== 3) {
    return {
      gross: emptySection(),
      net: emptySection(),
      scoringMode,
      hasAnyBet: false,
    };
  }

  // Course handicap cache for the net section.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of players) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const grossScoreFn = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    return sc.gross;
  };

  const netScoreFn = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  const runGross = scoringMode === "gross" || scoringMode === "netGross";
  const runNet = scoringMode === "net" || scoringMode === "netGross";

  const gross = runGross
    ? settleSection(
        course,
        players,
        pointBet,
        nassau,
        overallBet,
        frontBet,
        backBet,
        grossScoreFn,
      )
    : emptySection();

  const net = runNet
    ? settleSection(
        course,
        players,
        pointBet,
        nassau,
        overallBet,
        frontBet,
        backBet,
        netScoreFn,
      )
    : emptySection();

  return {
    gross,
    net,
    scoringMode,
    hasAnyBet: gross.hasAnyBet || net.hasAnyBet,
  };
}
